<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('ProjectSettings.VendorsTab.ProjectSupplierCloneModal.header.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <form
          action=""
          class="space-y-4"
          @submit.prevent="saveSupplier"
        >
          <label>
            <p>
              {{ $t('ProjectSettings.VendorsTab.ProjectSupplierCloneModal.form.vendorName') }}
            </p>

            <AppTypeAhead
              v-model="supplierForm.form.supplier_name"
              name="supplier_name"
              :suggestion-callback="getSuggestions"
              :initial-suggestions="getSuggestions"
              data-test="supplier-name"
              :error="supplierForm.getErrors('supplier_id') || supplierForm.getErrors('supplier_name') || supplierForm.getErrors('project_id')"
              class="w-full form-input"
              @value-selected="selectSupplier"
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <div class="space-x-2 ml-auto">
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="$emit('close'); supplierForm.resetData()"
          >
            {{ $t('ProjectSettings.VendorsTab.ProjectSupplierCloneModal.buttons.cancel') }}
          </button>

          <button
            type="button"
            class="btn btn--primary-blue"
            data-test="save-order"
            @click="saveSupplier"
          >
            {{ $t('ProjectSettings.VendorsTab.ProjectSupplierCloneModal.buttons.copyToProject') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { useCompanyProjectSupplierStore } from '@/store/companyProjectSupplier';
import { useSuppliersStore } from '@/store/supplier';
import Swal from 'sweetalert2';
import { nextTick, ref, watch } from 'vue';

const props = defineProps<{
  projectId: Id;
  companyId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const projectSuppliersStore = useCompanyProjectSupplierStore();

const supplierForm = useFormData({
  supplier_name: '',
  supplier_id: undefined,
  project_id: undefined,
});

watch(() => props.projectId, (newValue) => {
  supplierForm.form.project_id = newValue;
}, { immediate: true });

const cloneProjectSupplerAction = useStoreApiAction(projectSuppliersStore.cloneProjectSuppler);

const saveSupplier = () => {
  supplierForm.resetErrors();

  cloneProjectSupplerAction.request(props.companyId, supplierForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      text: t('ProjectSettings.VendorsTab.ProjectSupplierCloneModal.messages.vendorCloned'),
    }).then(() => {
      emits('close');
      supplierForm.resetData();
    });
  }).catch((error) => {
    supplierForm.setErrors(error.data);
  });
};

const suppliersStore = useSuppliersStore();
const suggestedSuppliers = ref<{ id: number; name: string }[]>([]);

const getSuggestions = async () => {
  suggestedSuppliers.value = await suppliersStore
    .search({ name: supplierForm.form.supplier_name, company_id: parseInt(`${props.companyId}`) });

  return suggestedSuppliers.value.map((value) => value.name);
};

const selectSupplier = (index: number) => {
  nextTick(() => {
    supplierForm.form.supplier_id = suggestedSuppliers.value[index].id;
  });
};
</script>

<style scoped></style>
